import { Base } from '@studiometa/js-toolkit';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
gsap.registerPlugin(ScrollTrigger);

/**
 * ScrollIntro component
 */
export default class ScreenIntro extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'ScreenIntro',
    refs: ['intro', 'phone', 'title', 'bg', 'summit', 'scroll'],
  };

  breakpoints = ['xxs', 'xs', 's'];

  /**
   * Check if device is mobile
   * @returns {boolean}
   */
  get isMobile() {
    return this.breakpoints.includes(this.$services.get('resized').breakpoint);
  }

  /**
   * Resized hook
   */
  resized() {
    this.adjustTitleSize();
  }

  /**
   * Mounted hook
   */
  async mounted() {
    this.p = this.$refs.intro.querySelector('p');
    this.cham = this.$refs.intro.querySelector('.cham');
    this.buyButton = document.querySelector('.header-buy-button');
    this.logos = document.querySelector('.header-logos');

    this.loadPhoneImage();
    await this.adjustTitleSize();
  }

  /**
   * Load phone image
   */
  loadPhoneImage() {
    if (this.isMobile) {
      this.$refs.phone.src = this.$refs.phone.dataset.srcMobile;
    } else {
      this.$refs.phone.src = this.$refs.phone.dataset.src;
    }
  }

  /**
   * Init scroll animation
   */
  init() {
    gsap.fromTo(
      this.$parent.$refs.overlayWrapper,
      {
        x: '0',
      },
      {
        x: '-100%',
        duration: 0.3,
        delay: 0.02,
        ease: 'power4.inOut',
        scrollTrigger: {
          trigger: '#intro-end',
          toggleActions: 'play none none reverse',
        },
      },
    );

    gsap.to(this.$refs.intro, {
      clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
      x: '-4%',
      duration: 0.2,
      ease: 'expo.inOut',
      scrollTrigger: {
        trigger: '#intro-end',
        toggleActions: 'play none none reverse',
      },
    });

    gsap.to(this.$refs.scroll, {
      clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
      duration: 0.4,
      ease: 'expo.inOut',
      scrollTrigger: {
        trigger: '#intro-end',
        toggleActions: 'play none none reverse',
      },
    });

    gsap.to(this.$refs.phone, {
      opacity: 0,
      duration: 0.4,
      scrollTrigger: {
        trigger: '#intro-end-2',
        toggleActions: 'play none none reverse',
      },
    });

    gsap.to(this.$parent.vidintro, {
      opacity: 0,
      duration: 0.3,
      ease: 'expo.out',
      scrollTrigger: {
        trigger: '#intro-end-2',
        toggleActions: 'play none none reverse',
      },
      onComplete: () => {
        this.$parent.vidintro.play();
      },
    });
  }

  /**
   * Service scrolled hook
   * @param {object} props Props
   * @param {number} props.y The current vertical scroll position.
   * @param {object} props.changed For both axis, whether or not the current position has changed since the last one.
   */
  scrolled({ y, changed }) {
    if (y === 0 && changed.y) {
      gsap.set(this.$parent.vidintro, {
        opacity: 1,
      });
      gsap.set(this.$refs.phone, {
        opacity: 1,
      });

      gsap.to(this.$refs.scroll, {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
      });

      gsap.to(this.$refs.summit, {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
      });
    }
  }

  /**
   * @see index.js onVideosReady
   */
  onWindowVideosready() {
    // Display only if not scrolling
    if (scrollY !== 0) {
      gsap.set([this.$parent.vidintro, this.$refs.phone], {
        opacity: 0,
        scale: 1,
        y: 0,
      });

      gsap.set(
        [
          this.$refs.title,
          this.$refs.intro.querySelector('p'),
          this.$refs.intro.querySelector('.cham'),
          this.$parent.$refs.overlay,
          this.$parent.$refs.summit,
        ],
        {
          x: '0',
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        },
      );

      gsap.set(this.$refs.scroll, {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
      });

      gsap.fromTo(
        this.buyButton,
        {
          clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
          x: '-20%',
        },
        {
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
          x: '0',
          duration: 0.24,
          ease: 'power2.out',
        },
        '-=0.1',
      );

      gsap.fromTo(
        this.logos,
        {
          clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
          x: '-20%',
        },
        {
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
          x: '0',
          duration: 0.24,
          ease: 'power2.out',
        },
        '-=0.1',
      );

      this.init();
      return;
    }

    const tl = gsap.timeline();
    tl.set(this.$parent.vidintro, {
      opacity: 1,
    });

    tl.to(this.$refs.phone, {
      opacity: 1,
      scale: 1,
      y: 0,
      duration: 0.7,
      ease: 'power4.out',
    });

    tl.fromTo(
      this.$parent.$refs.overlay,
      {
        x: '-100%',
        ease: 'expo.inOut',
      },
      {
        x: '0',
        duration: 0.45,
        ease: 'expo.out',
      },
      '-=0.4',
    );

    tl.fromTo(
      this.$refs.title,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-10%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.2,
        ease: 'power2.out',
      },
      '-=0.25',
    );

    tl.fromTo(
      this.p,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-20%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.2,
        ease: 'power2.out',
      },
      '-=0.1',
    );

    tl.fromTo(
      this.cham,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-20%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.24,
        ease: 'power2.out',
      },
      '-=0.14',
    );

    tl.fromTo(
      this.buyButton,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-20%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.24,
        ease: 'power2.out',
      },
      '-=0.1',
    );

    tl.fromTo(
      this.logos,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-20%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.24,
        ease: 'power2.out',
      },
      '-=0.1',
    );

    tl.fromTo(
      this.$refs.summit,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        duration: 0.24,
        ease: 'power2.out',
      },
      '-=0.1',
    );

    tl.fromTo(
      this.$refs.scroll,
      {
        clipPath: 'polygon(0 0, 0% 0, 0% 100%, 0% 100%)',
        x: '-20%',
      },
      {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)',
        x: '0',
        duration: 0.24,
        ease: 'power2.out',
      },
      '-=0.1',
    );

    this.init();
  }

  /**
   * Adjust title size to fill container width
   */
  adjustTitleSize() {
    if (!this.$refs.title) {
      return;
    }

    const container = this.$refs.title.parentElement;
    const containerWidth = container.clientWidth;
    const padding =
      Number.parseFloat(getComputedStyle(container).paddingLeft) +
      Number.parseFloat(getComputedStyle(container).paddingRight);
    const availableWidth = containerWidth - padding;

    let minFontSize = 10;
    let maxFontSize = 500;
    let fontSize = 0;

    while (minFontSize <= maxFontSize) {
      fontSize = Math.floor((minFontSize + maxFontSize) / 2);
      this.$refs.title.style.fontSize = `${fontSize}px`;

      if (this.$refs.title.scrollWidth > availableWidth) {
        maxFontSize = fontSize - 1;
      } else {
        minFontSize = fontSize + 1;
      }
    }

    this.$refs.title.style.fontSize = `${fontSize}px`;
  }
}
